import { useMemo } from 'react';
import { useTiclickTableContext } from '@src/design-system/molecules/ticlick-table/context/ticlick-table-context-hook';

import isEqual from 'lodash/isEqual';

import { deduplicateArr } from '@shared/utils/helpers/deduplicate-arr';

import type { TView } from '@shared/types/view';
import type { TDynamicFilter } from '@shared/types/dynamic-filter';
import type { TCustomFieldFilter } from '@shared/types/custom-field-filter';
import { removeNullishFromObj } from '@src/shared/utils/helpers/remove-nullish-from-obj';
import { useTableStates } from './use-table-states';

type TStaticFilter = TView['static_filters'][number];

export const useTableViewHelpers = () => {
  const { table } = useTiclickTableContext();

  const { tableFilters, setTableFilters, currentView } = useTableStates();

  const isDirty = useMemo(() => {
    const viewFilters = removeNullishFromObj({
      staticFilters: currentView?.static_filters,
      customFilters: currentView?.custom_filters.filter((f) => f.id),
      dynamicFilters: currentView?.filters.filter((f) => f.id),
    });

    const currentFilters = removeNullishFromObj({
      staticFilters: tableFilters?.staticFilters,
      customFilters: tableFilters?.customFieldFilters?.filter((f) => f.id),
      dynamicFilters: tableFilters?.dynamicFilters?.filter((f) => f.id),
    });

    return !isEqual(viewFilters, currentFilters);
  }, [
    currentView?.static_filters,
    currentView?.custom_filters,
    currentView?.filters,
    tableFilters?.staticFilters,
    tableFilters?.customFieldFilters,
    tableFilters?.dynamicFilters,
  ]);

  // Static filters helpers
  const addStaticFilter = (staticFilter: TStaticFilter) => {
    const prevStaticFilters = tableFilters?.staticFilters || [];
    setTableFilters((prev) => ({
      ...prev,
      staticFilters: deduplicateArr(
        [...prevStaticFilters, staticFilter],
        'field'
      ),
    }));
  };

  const resetFiltersToOriginalViewFilters = () => {
    setTableFilters({
      staticFilters: currentView?.static_filters,
      dynamicFilters: currentView?.filters,
      customFieldFilters: currentView?.custom_filters,
    });
  };

  const clearAllFilters = () => {
    setTableFilters({
      staticFilters: [],
      dynamicFilters: [],
      customFieldFilters: [],
    });
  };

  const clearMultipleFacets = (fields: string[]) => {
    const updatedStaticFilters = tableFilters?.staticFilters?.filter(
      (f) => !fields.includes(f.field)
    );

    setTableFilters((prev) => ({
      ...prev,
      staticFilters: updatedStaticFilters || [],
    }));
  };

  const clearOneValue = (
    field: string,
    valueToRemove: string | number | null
  ) => {
    const existingFilter = tableFilters?.staticFilters?.find(
      (filter) => filter.field === field
    );

    if (!existingFilter) return;

    const updatedValues = existingFilter.value.filter(
      (value) => String(value) !== String(valueToRemove)
    );

    setFacetValue({
      ...existingFilter,
      value: updatedValues,
    });
  };

  const setFacetValue = (staticFilter: TStaticFilter) => {
    return staticFilter.value.length
      ? addStaticFilter(staticFilter)
      : clearMultipleFacets([staticFilter.field]);
  };

  const setMultipleFacetValues = (staticFilters: TStaticFilter[]) => {
    setTableFilters((prevView) => {
      const prevStaticFilters = prevView?.staticFilters || [];

      const updatingFields = staticFilters.map((f) => f.field);

      const filteredFilters = prevStaticFilters.filter(
        (filter) => !updatingFields.includes(filter.field)
      );

      const filtersWithValues = staticFilters.filter((f) => f.value.length > 0);

      return {
        ...prevView,
        staticFilters: [...filteredFilters, ...filtersWithValues],
      };
    });
  };

  // Dynamic filters helpers
  const addDynamicFilter = (dynamicFilter: TDynamicFilter) => {
    const prevDynamicFilters = tableFilters?.dynamicFilters || [];
    setTableFilters((prev) => ({
      ...prev,
      dynamicFilters: [...prevDynamicFilters, dynamicFilter],
    }));
  };

  const updateDynamicFilter = (
    dynamicFilter: Partial<TDynamicFilter>,
    position: number
  ) => {
    const newArr = tableFilters?.dynamicFilters?.map((item, index) => {
      return index === position ? { ...item, ...dynamicFilter } : item;
    });

    setTableFilters((prev) => ({
      ...prev,
      dynamicFilters: newArr || [],
    }));
  };

  const deleteDynamicFilter = (position: number) => {
    const newArr = tableFilters?.dynamicFilters?.filter((item, index) => {
      return position !== index;
    });

    setTableFilters((prev) => ({
      ...prev,
      dynamicFilters: newArr || [],
    }));
  };

  // Custom Field filters helpers
  const addCustomFieldFilter = (dynamicFilter: TCustomFieldFilter) => {
    const prevDynamicFilters = tableFilters?.customFieldFilters || [];
    setTableFilters((prev) => ({
      ...prev,
      customFieldFilters: [...prevDynamicFilters, dynamicFilter],
    }));
  };

  const updateCustomFieldFilter = (
    dynamicFilter: Partial<TCustomFieldFilter>,
    position: number
  ) => {
    const newArr = tableFilters?.customFieldFilters?.map((item, index) => {
      return index === position ? { ...item, ...dynamicFilter } : item;
    });

    setTableFilters((prev) => ({
      ...prev,
      customFieldFilters: newArr || [],
    }));
  };

  const deleteCustomFieldFilter = (position: number) => {
    const newArr = tableFilters?.customFieldFilters?.filter((item, index) => {
      return position !== index;
    });

    setTableFilters((prev) => ({
      ...prev,
      customFieldFilters: newArr || [],
    }));
  };

  const getSelectedStaticFilterFacet = (facetName: string) => {
    return tableFilters?.staticFilters?.find((f) => f.field === facetName);
  };

  return {
    isDirty,
    addStaticFilter,
    resetFilters: table.resetGlobalFilter,
    resetFiltersToOriginalViewFilters,
    clearAllFilters,
    setFacetValue,
    clearOneValue,
    clearMultipleFacets,
    setMultipleFacetValues,
    addDynamicFilter,
    updateDynamicFilter,
    deleteDynamicFilter,
    addCustomFieldFilter,
    updateCustomFieldFilter,
    deleteCustomFieldFilter,
    getSelectedStaticFilterFacet,
  };
};
